import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatInTimeZone } from 'date-fns-tz';
import { useSessionStore, OPERATOR_NUMBERS } from '../../store/sessionStore.js';

/**
 * Operator login/logout board (foglio "PULSANTE OPERATORI" del file Modifiche20260722.xlsx):
 * OPE1..OPE8 in two columns, each with its login timestamp and its own LOGIN/LOGOUT buttons,
 * so several operators can work on the same order at the same time.
 */
export default function OperatorsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    operators, isSessionActive, plantTimezone,
    refreshOperators, loginOperator, logoutOperator,
  } = useSessionStore();
  const [busy, setBusy] = useState<number | null>(null);

  useEffect(() => { refreshOperators().catch(() => {}); }, [refreshOperators]);

  const openLogFor = (n: number) => operators.find(o => o.operatorNumber === n && o.logoutAt === null) ?? null;

  async function handle(action: 'login' | 'logout', n: number) {
    setBusy(n);
    try {
      if (action === 'login') await loginOperator(n);
      else await logoutOperator(n);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="h-full flex flex-col p-4">
      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={() => navigate('/workstation')}
          className="bg-gray-700 hover:bg-gray-600 active:bg-gray-500 text-white px-5 py-3 rounded-lg font-bold text-sm"
        >
          {t('operator.back')}
        </button>
        <h1 className="text-white font-bold text-lg">{t('shell.operators')}</h1>
      </div>

      {!isSessionActive && (
        <p className="text-amber-400 text-sm mb-3">{t('operator.needOrder')}</p>
      )}

      {/* colonne come nel foglio: OPE1-4 a sinistra, OPE5-8 a destra */}
      <div className="grid grid-cols-2 grid-rows-4 grid-flow-col gap-3">
        {OPERATOR_NUMBERS.map(n => {
          const open = openLogFor(n);
          return (
            <div
              key={n}
              className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
                open ? 'bg-green-900/40 border-green-600' : 'bg-gray-900 border-gray-700'
              }`}
            >
              <span className="text-white font-bold text-lg w-24 shrink-0">
                {t('operator.short')}{n}
              </span>
              <span className="flex-1 text-sm font-mono text-gray-300">
                {open ? formatInTimeZone(new Date(open.loginAt), plantTimezone, 'dd/MM HH:mm') : '—'}
              </span>
              <button
                onClick={() => handle('login', n)}
                disabled={!isSessionActive || open !== null || busy === n}
                className="bg-green-600 hover:bg-green-500 active:bg-green-400 disabled:bg-gray-700 disabled:text-gray-500 text-white px-6 py-4 rounded-lg font-bold text-base"
              >
                {t('operator.login')}
              </button>
              <button
                onClick={() => handle('logout', n)}
                disabled={!isSessionActive || open === null || busy === n}
                className="bg-red-700 hover:bg-red-600 active:bg-red-500 disabled:bg-gray-700 disabled:text-gray-500 text-white px-6 py-4 rounded-lg font-bold text-base"
              >
                {t('operator.logout')}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
